// Lightweight color mapping utilities for deterministic, stable assignments
// and mapping table maintenance stored in layout.meta.lumi.theme

export type LumiTheme = {
  mode?: 'categorical' | 'sequential' | 'diverging';
  name?: string;
  swatches?: string[];
  opacity?: number;
  background?: string;
  text?: string;
  grid?: string;
  sequential?: {
    scale?: string;
    reversed?: boolean;
    range?: 'data' | 'custom';
    min?: number | null;
    max?: number | null;
  };
  diverging?: {
    scale?: string;
    reversed?: boolean;
    midpoint?: number | null;
    symmetric?: boolean;
    min?: number | null;
    max?: number | null;
  };
  coloraxis?: string;
  mappings?: {
    by?: string | null;
    domain?: (string | number)[];
    colors?: Record<string, string>;
    locked?: (string | number)[];
    missing?: string; // color used for unseen values (until assigned)
  };
  roles?: Record<string, string>;
  pattern?: { enabled?: boolean; by_value?: Record<string, string> };
  seed?: number;
  notes?: { color_adjustments?: any[]; clamp?: boolean; new_categories?: any[] };
};

// Simple deterministic string hash (32-bit) for stable color indexing
export function stableHash(input: string): number {
  let h = 2166136261 >>> 0; // FNV-1a basis
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function ensureThemeDefaults(theme: Partial<LumiTheme> | undefined): LumiTheme {
  const t: LumiTheme = {
    mode: theme?.mode || 'categorical',
    name: theme?.name || 'Custom',
    swatches: Array.isArray(theme?.swatches) && theme!.swatches!.length > 0
      ? theme!.swatches!
      : ['#E69F00','#56B4E9','#009E73','#F0E442','#0072B2','#D55E00','#CC79A7','#999999'],
    opacity: typeof theme?.opacity === 'number' ? theme!.opacity! : 0.9,
    background: theme?.background || '#ffffff',
    text: theme?.text || '#111111',
    grid: theme?.grid || '#e6e6e6',
    sequential: {
      scale: theme?.sequential?.scale || 'Viridis',
      reversed: !!theme?.sequential?.reversed,
      range: theme?.sequential?.range || 'data',
      min: theme?.sequential?.min ?? null,
      max: theme?.sequential?.max ?? null
    },
    diverging: {
      scale: theme?.diverging?.scale || 'RdBu',
      reversed: !!theme?.diverging?.reversed,
      midpoint: theme?.diverging?.midpoint ?? 0,
      symmetric: theme?.diverging?.symmetric !== false,
      min: theme?.diverging?.min ?? null,
      max: theme?.diverging?.max ?? null
    },
    coloraxis: theme?.coloraxis || 'coloraxis',
    mappings: {
      by: theme?.mappings?.by ?? null,
      domain: Array.isArray(theme?.mappings?.domain) ? theme!.mappings!.domain! : [],
      colors: theme?.mappings?.colors || {},
      locked: Array.isArray(theme?.mappings?.locked) ? theme!.mappings!.locked! : [],
      missing: theme?.mappings?.missing || '#9e9e9e'
    },
    roles: theme?.roles || {
      highlight: '#FFD54F',
      selection: '#FF7043',
      reference: '#37474F',
      ci_band: 'rgba(0,0,0,0.15)'
    },
    pattern: theme?.pattern || { enabled: false, by_value: {} },
    seed: typeof theme?.seed === 'number' ? theme!.seed! : Math.floor(Math.random() * 2 ** 31),
    notes: theme?.notes || { color_adjustments: [], clamp: false, new_categories: [] }
  };
  return t;
}

// Build a mapping for a set of values using locked + explicit colors first
export function buildDeterministicMapping(
  theme: LumiTheme,
  values: Array<string | number>
): Record<string | number, string> {
  const swatches = theme.swatches || [];
  const lockedSet = new Set(theme.mappings?.locked || []);
  const existing = theme.mappings?.colors || {};
  const seed = String(theme.seed ?? 0);

  const result: Record<string | number, string> = { ...existing };
  const domain = Array.from(new Set(values));

  for (const v of domain) {
    const key = String(v);
    if (existing[key]) {
      // keep existing explicit color
      continue;
    }
    if (lockedSet.has(v)) {
      // try find prior color in swatches by stable index if absent
      const idx = stableHash(key + seed) % Math.max(1, swatches.length);
      result[key] = swatches[idx];
      continue;
    }
    const idx = stableHash(key + seed) % Math.max(1, swatches.length);
    result[key] = swatches[idx];
  }

  return result;
}

// Append unseen values to domain and optionally assign colors deterministically
export function assignNewDomainValues(
  theme: LumiTheme,
  values: Array<string | number>
): { domain: (string | number)[]; colors: Record<string, string> } {
  const currentDomain = Array.isArray(theme.mappings?.domain) ? [...theme.mappings!.domain!] : [];
  const existing = theme.mappings?.colors || {};
  const seen = new Set(currentDomain.map(String));
  const toAppend = values.filter(v => !seen.has(String(v)));

  if (toAppend.length === 0) {
    return { domain: currentDomain, colors: existing };
    
  }

  const mergedValues = [...currentDomain, ...toAppend];
  const newColors = { ...existing, ...buildDeterministicMapping(ensureThemeDefaults(theme), toAppend) };
  return { domain: mergedValues, colors: newColors };
}

// Clip numeric arrays to [min,max] when clamp is enabled
export function clampArray(values: number[], min?: number | null, max?: number | null): number[] {
  if (!Array.isArray(values)) return values as any;
  const lo = typeof min === 'number' ? min : -Infinity;
  const hi = typeof max === 'number' ? max : Infinity;
  return values.map(v => (v < lo ? lo : v > hi ? hi : v));
}


